import type { List as ImList, Map } from 'immutable';
import lodash from 'lodash';
import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import AddCircleIcon from '@mui/icons-material/AddCircle';
import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import type {
  AutocompleteProps,
  AutocompleteRenderGroupParams,
  AutocompleteRenderInputParams,
} from '@mui/material/Autocomplete';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import type { TextFieldProps } from '@mui/material/TextField';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddCustomerDrawer from 'components/Drawers/Customers/CustomerDrawer';

import { ListSubheader, Stack } from '@mui/material';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import type { PopperProps } from '@mui/material/Popper';
import Popper from '@mui/material/Popper';
import type { FilterOptionsState } from '@mui/material/useAutocomplete';
import { customersSelectors, customersTypes } from 'app-components/flux/customers';
import { projectsSelectors, projectsTypes } from 'app-components/flux/projects';
import { useAppSelector } from 'hooks/redux';

export type CustomerProjectSelectorValue = {
  customer: customersTypes.Customer | null;
  project: projectsTypes.Project | null;
};

export type CustomerSelectorProps = Partial<AutocompleteProps<CustomerProjectSelectorValue, false, true, false>> & {
  onChange: (event: React.SyntheticEvent, value: CustomerProjectSelectorValue) => void;
  sx?: any;
  textFieldProps?: TextFieldProps;
  value: CustomerProjectSelectorValue;
  disableAddCustomer?: boolean;
  showAddNewClient?: boolean;
};

// TODO: These should really be part of the Projects module
//
type ProjectsByStatus = {
  allProjects?: ImList<projectsTypes.Project>;
  completedProjects?: ImList<projectsTypes.Project>;
  activeProjects?: ImList<projectsTypes.Project>;
};

type ProjectsMap = Map<string, ProjectsByStatus>;

const AutoWidthPopper = (props: PopperProps) => {
  return <Popper {...props} placement='bottom-start' style={{ width: 'auto', minWidth: 100 }} />;
};

const AddFilterClientId = 'add-new-client';

const GUEST_LABEL = 'Choose Guest for Generic Sales & Billing';
const CLIENTS_LABEL = 'Clients & Projects';

const GroupHeader = (props: AutocompleteRenderGroupParams) => {
  if (props.group === 'none') {
    return <React.Fragment key={props.key}>{props.children}</React.Fragment>;
  }

  return (
    <Stack key={props.key}>
      <ListSubheader
        disableSticky
        sx={{
          typography: 'captionRegular',
          color: 'textSecondary',
          pt: 1,
          ml: -1.5,
        }}
      >
        {props.group === GUEST_LABEL ? GUEST_LABEL : CLIENTS_LABEL}
      </ListSubheader>
      <List component='ul' disablePadding sx={{ p: 0, m: 0 }}>
        {props.children}
      </List>
    </Stack>
  );
};

export default function CustomerAndProjectSelector(props: CustomerSelectorProps) {
  const {
    onChange,
    options: discardedOptions,
    renderInput,
    sx: otherSx,
    textFieldProps,
    value,
    disableAddCustomer = false,
    showAddNewClient = false,
    disabled,
    ...other
  } = props;

  const customersById = useSelector(customersSelectors.getCustomers);
  const projectsByCustomerId: ProjectsMap = useSelector(projectsSelectors.getProjectsByCustomerId);
  const isNotClearable = !value.customer && !value.project;
  const [searchParams, setSearchParams] = useSearchParams();
  const hasGuestClient = useAppSelector(customersSelectors.getGuestCustomer);

  const getCustomerLabel = useCallback(
    (customer: customersTypes.Customer | null) =>
      customer?.businessName?.trim() ||
      `${customer?.firstName?.trim() || ''} ${customer?.lastName?.trim() || ''}` ||
      'Unknown',
    [],
  );

  const getOptionLabel = useCallback(
    ({ customer, project }: CustomerProjectSelectorValue) => {
      // no option selected (value is null)
      if (customer === undefined) {
        return '';
      }
      // no customer so we assume we are adding a new customer
      if (customer === null && !disableAddCustomer) {
        return '+ Add New';
      }
      // if project is null, option is just customer
      if (project === null) {
        return getCustomerLabel(customer);
      }
      return project?.name || 'Unknown Project';
    },
    [getCustomerLabel, disableAddCustomer],
  );

  const getValueToRender = useCallback(
    ({ customer, project }: CustomerProjectSelectorValue) => {
      // no option selected (value is null or customer is null) - also handles when add-new is selected
      if (!customer?.id) {
        return '';
      }
      let valueToRender = getCustomerLabel(customer);
      if (project) {
        valueToRender += `: ${project.name}`;
      }
      return valueToRender;
    },
    [getCustomerLabel],
  );

  const options = useMemo(() => {
    const values: CustomerProjectSelectorValue[] = customersById
      .valueSeq()
      .sort((a, b) => {
        if (a.isGuest && !b.isGuest) return 1;
        if (!a.isGuest && b.isGuest) return -1;
        return getCustomerLabel(a).localeCompare(getCustomerLabel(b));
      })
      .reduce((acc: CustomerProjectSelectorValue[], customer) => {
        acc.push({ customer, project: null });
        const projects = projectsByCustomerId.get(customer.id as string)?.activeProjects;
        if (projects) {
          projects
            .sort((a, b) => (a.name as string).localeCompare(b.name as string))
            .forEach((project) => acc.push({ customer, project }));
        }
        return acc;
      }, []);
    return values;
  }, [customersById, getCustomerLabel, projectsByCustomerId]);

  const addCustomer = useCallback(
    (businessName?: string) => {
      searchParams.set('editCustomer', 'create');
      if (businessName) {
        searchParams.set('businessName', businessName);
      }
      setSearchParams(searchParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const handleAdd = useCallback(
    (customer: any) => {
      const newValue = { customer, project: null };

      const event = {
        currentTarget: { value: newValue },
        target: { value: newValue },
        preventDefault() {},
        stopPropagation() {},
        type: 'change',
      } as unknown as React.SyntheticEvent;

      onChange(event, newValue);
    },
    [onChange],
  );

  const handleChange = useCallback(
    (event: React.SyntheticEvent, newValue: CustomerProjectSelectorValue) => {
      if (!newValue) {
        // values cleared
        onChange(event, { customer: null, project: null });
      } else {
        const { customer, project } = newValue;
        if (!customer && !project) {
          addCustomer();
          return;
        } else if (customer?.id === AddFilterClientId) {
          addCustomer(customer.firstName);
          return;
        }
        onChange(event, newValue);
      }
    },
    [addCustomer, onChange],
  );

  const handleFilterOptions = useCallback(
    (
      optionsToFilter: CustomerProjectSelectorValue[],
      params: FilterOptionsState<CustomerProjectSelectorValue>,
    ): CustomerProjectSelectorValue[] => {
      const filtered = createFilterOptions()(optionsToFilter, params) as CustomerProjectSelectorValue[];
      const AddFilteredOption: CustomerProjectSelectorValue[] = showAddNewClient
        ? [
            {
              customer: customersTypes.mkCustomer({
                id: AddFilterClientId,
                businessName: params.inputValue ? `Add '${params.inputValue}' Client` : 'New Client',
                firstName: params.inputValue,
              }),
              project: null,
            },
          ]
        : [];

      // Get guest if it exists so we can put it last
      let guest: CustomerProjectSelectorValue[] = [];
      if (filtered.length && filtered[filtered.length - 1]?.customer?.isGuest) {
        const last = filtered.pop();
        guest = last ? [last] : [];
      }

      const result = [...filtered, ...AddFilteredOption, ...guest];

      return result;
    },
    [showAddNewClient],
  );

  const renderInputInternal = (params: AutocompleteRenderInputParams) => (
    <TextField label='Client & Project' {...lodash.merge(params, textFieldProps)} />
  );

  const fixedValue =
    value.customer === null
      ? { customer: new customersTypes.mkCustomer(), project: new projectsTypes.mkProject() }
      : value;

  const groupByFn = useCallback((option: CustomerProjectSelectorValue) => {
    return option.customer?.isGuest ? GUEST_LABEL : CLIENTS_LABEL;
  }, []);

  return (
    <>
      <Autocomplete<CustomerProjectSelectorValue, false, boolean, false>
        data-testid='client-project-select'
        autoComplete
        autoHighlight
        filterOptions={handleFilterOptions}
        getOptionLabel={getValueToRender}
        id='merchant-autocomplete'
        isOptionEqualToValue={(option: CustomerProjectSelectorValue, valueArg: CustomerProjectSelectorValue) => {
          if (!option.customer?.id || !valueArg.customer?.id) {
            return false;
          }
          return option?.customer?.id === valueArg?.customer?.id && option?.project?.id === valueArg?.project?.id;
        }}
        multiple={false}
        onChange={handleChange}
        options={options}
        groupBy={hasGuestClient ? groupByFn : undefined}
        renderGroup={GroupHeader}
        renderOption={(propsArg, option: CustomerProjectSelectorValue) => {
          const { customer, project } = option;
          const key = project?.id || customer?.id || 'add-new';
          const isProject = !!project;
          return (
            <ListItem {...propsArg} key={key}>
              {customer?.id === AddFilterClientId ? (
                <Typography
                  variant='button'
                  sx={{
                    color: 'primary.main',
                    textTransform: 'none',
                    alignItems: 'center',
                    display: 'flex',
                    gap: 1,
                  }}
                >
                  <AddCircleIcon />
                  {customer?.businessName}
                </Typography>
              ) : (
                <Typography
                  sx={{ marginLeft: isProject ? '16px' : '0px' }}
                  variant={!isProject ? 'bodyMedium' : 'bodyRegular'}
                >
                  {getOptionLabel(option)}
                </Typography>
              )}
            </ListItem>
          );
        }}
        PopperComponent={AutoWidthPopper}
        renderInput={renderInput || renderInputInternal}
        sx={otherSx}
        value={fixedValue}
        disabled={disabled}
        disableClearable={isNotClearable}
        clearIcon={<ClearRoundedIcon sx={{ height: 16, width: 16 }} />}
        {...other}
      />
      {/* Modal >1200 to go above invoice modal, but <1400 to go below nested MUI menus */}
      {!disableAddCustomer && <AddCustomerDrawer onUpsert={handleAdd} sx={{ zIndex: (theme) => theme.zIndex.modal }} />}
    </>
  );
}
